import { useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { useQuery } from "@pos/shared/query";
import { getDashboardOps } from "@pos/shared/api";
import { useAuthStore } from "@pos/shared/stores";
import type { DashboardOpsData } from "@pos/shared/types";
import { DatePicker, Button } from "@pos/ui";
import {
  Boxes,
  Download,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Trash2,
  ShoppingCart,
  Package,
  Percent,
  Wine,
  GlassWater,
  Receipt,
  Loader2,
  BarChart3,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────

// Flat shape kept for the renderer's convenience. The v2 endpoint
// returns a structured object; we project it down here so the existing
// JSX can stay terse. See pos-rest-api `dashboard.controller.ts`.
interface OpsFlat {
  total_stock: number;
  alc_valuation: number;
  nalc_valuation: number;
  alc_purchase_amt: number;
  nalc_purchase_amt: number;
  alc_price_up_cnt: number;
  alc_price_down_cnt: number;
  alc_val_change: number;
  nalc_price_up_cnt: number;
  nalc_price_down_cnt: number;
  nalc_val_change: number;
  sales_today: number;
  sales_mtd: number;
  coll_cash: number;
  coll_card: number;
  coll_upi: number;
  expenses: number;
  discounts: number;
  del_sales_cnt: number;
  del_sales_value: number;
  del_alc_pur_cnt: number;
  del_alc_pur_val: number;
  del_nalc_pur_cnt: number;
  del_nalc_pur_val: number;
}

function flattenOps(d: DashboardOpsData): OpsFlat {
  // Defensive against partial backend rollouts (e.g. staging without the
  // sales block yet). Older deploys may omit sub-objects entirely; we
  // fall back to 0 rather than throwing during render.
  return {
    total_stock: d.stock?.totalUnits ?? 0,
    alc_valuation: d.stock?.alcoholValue ?? 0,
    nalc_valuation: d.stock?.nonAlcoholValue ?? 0,
    alc_purchase_amt: d.purchases?.alcoholAmount ?? 0,
    nalc_purchase_amt: d.purchases?.nonAlcoholAmount ?? 0,
    alc_price_up_cnt: d.priceMovement?.alcohol?.upCount ?? 0,
    alc_price_down_cnt: d.priceMovement?.alcohol?.downCount ?? 0,
    alc_val_change: d.priceMovement?.alcohol?.valuationChange ?? 0,
    nalc_price_up_cnt: d.priceMovement?.nonAlcohol?.upCount ?? 0,
    nalc_price_down_cnt: d.priceMovement?.nonAlcohol?.downCount ?? 0,
    nalc_val_change: d.priceMovement?.nonAlcohol?.valuationChange ?? 0,
    sales_today: d.sales?.today ?? 0,
    sales_mtd: d.sales?.monthToDate ?? 0,
    coll_cash: d.sales?.collections?.cash ?? 0,
    coll_card: d.sales?.collections?.card ?? 0,
    coll_upi: d.sales?.collections?.upi ?? 0,
    expenses: d.expenses ?? 0,
    discounts: d.discounts ?? 0,
    del_sales_cnt: d.cancellations?.sales?.count ?? 0,
    del_sales_value: d.cancellations?.sales?.value ?? 0,
    del_alc_pur_cnt: d.cancellations?.alcoholPurchases?.count ?? 0,
    del_alc_pur_val: d.cancellations?.alcoholPurchases?.value ?? 0,
    del_nalc_pur_cnt: d.cancellations?.nonAlcoholPurchases?.count ?? 0,
    del_nalc_pur_val: d.cancellations?.nonAlcoholPurchases?.value ?? 0,
  };
}

const fmtINR = (v: number): string =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(v);

const fmtNum = (v: number): string => new Intl.NumberFormat("en-IN").format(v);

// ── Sub-components ───────────────────────────────────────

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">
          {label}
        </span>
        <div className="flex-1" />
      </div>
      {children}
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
  icon: Icon,
  tint,
  large,
  valueColor,
  action,
  children,
}: {
  label: string;
  value?: string;
  sub?: string;
  icon?: React.ComponentType<{ className?: string }>;
  tint?: string;
  large?: boolean;
  valueColor?: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className={`rounded-xl p-4 ${tint ?? "bg-muted/30"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="size-4 text-muted-foreground/60" />}
          <div>
            <p className="text-sm font-medium text-foreground">{label}</p>
            {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
          </div>
        </div>
        {action}
      </div>
      {value !== undefined && (
        <p
          className={`mt-3 tabular-nums tracking-tight font-semibold ${
            large ? "text-2xl" : "text-xl"
          } ${valueColor ?? ""}`}
        >
          {value}
        </p>
      )}
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={`tabular-nums ${bold ? "text-sm font-semibold" : "text-sm font-medium"}`}
      >
        {value}
      </span>
    </div>
  );
}

function DlBtn({ onClick }: { onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-md text-muted-foreground/50 transition-colors hover:bg-foreground/5 hover:text-muted-foreground"
    >
      <Download className="size-3.5" />
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────

export function DashboardPage() {
  const shopId = useAuthStore((s) => s.shopId);

  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const opsQuery = useQuery({
    queryKey: ["ops-dashboard", shopId, selectedDate],
    queryFn: async () => {
      const res = await getDashboardOps(selectedDate);
      setLastUpdated(new Date());
      return res;
    },
    enabled: !!shopId,
  });

  const ops: OpsFlat | null = useMemo(() => {
    if (opsQuery.data?.data) return flattenOps(opsQuery.data.data);
    return null;
  }, [opsQuery.data]);

  const handleRefresh = useCallback(() => {
    void opsQuery.refetch();
  }, [opsQuery]);

  if (opsQuery.isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading dashboard</p>
      </div>
    );
  }

  if (!ops) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <BarChart3 className="size-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Failed to load dashboard
        </p>
      </div>
    );
  }

  const d: OpsFlat = ops;

  return (
    <div className="flex h-full flex-col overflow-auto">
      {/* Toolbar */}
      <div className="flex items-center justify-end gap-2 border-b border-border/60 px-5 py-2">
        <DatePicker
          value={selectedDate}
          onChange={setSelectedDate}
          className="w-36"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={opsQuery.isFetching}
          className="gap-1.5 text-xs text-muted-foreground"
        >
          <RefreshCw
            className={`size-3 ${opsQuery.isFetching ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
        {lastUpdated && (
          <span className="text-[11px] tabular-nums text-muted-foreground/60">
            {format(lastUpdated, "HH:mm")}
          </span>
        )}
      </div>

      {/* Dashboard content */}
      <div className="mx-auto w-full max-w-6xl flex-1 space-y-6 p-5">
        {/* ── SALES ──────────────────────────────────────── */}
        <Section label="Sales">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Metric
              label="Today"
              sub="Net sales for selected date"
              icon={TrendingUp}
              tint="bg-emerald-500/5"
              value={fmtINR(d.sales_today)}
              valueColor="text-emerald-600"
              large
            />
            <Metric
              label="Month to Date"
              sub="Cumulative this month"
              icon={BarChart3}
              tint="bg-blue-500/5"
              value={fmtINR(d.sales_mtd)}
              large
            />
            <Metric label="Collections" sub="By tender mode" icon={Receipt}>
              <div className="space-y-0.5">
                <Row label="Cash" value={fmtINR(d.coll_cash)} />
                <Row label="Card" value={fmtINR(d.coll_card)} />
                <Row label="UPI" value={fmtINR(d.coll_upi)} />
              </div>
            </Metric>
            <Metric
              label="Total Collected"
              sub="Cash + Card + UPI"
              icon={Receipt}
              tint="bg-emerald-500/5"
              value={fmtINR(d.coll_cash + d.coll_card + d.coll_upi)}
            />
          </div>
        </Section>

        {/* ── STORE OPERATIONS ──────────────────────────── */}
        <Section label="Store Operations">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Metric
              label="Total Stock"
              sub="Number of Materials"
              icon={Boxes}
              value={fmtNum(d.total_stock)}
              large
            />
            <Metric
              label="ALC Valuation"
              sub="Alcohol Materials"
              icon={Wine}
              tint="bg-violet-500/5"
              value={fmtINR(d.alc_valuation)}
            />
            <Metric
              label="NALC Valuation"
              sub="Non-Alcohol Materials"
              icon={GlassWater}
              tint="bg-cyan-500/5"
              value={fmtINR(d.nalc_valuation)}
            />
            <Metric label="Purchases" icon={ShoppingCart} tint="bg-amber-500/5">
              <div className="space-y-0.5">
                <Row label="ALC" value={fmtINR(d.alc_purchase_amt)} />
                <Row label="NALC" value={fmtINR(d.nalc_purchase_amt)} />
              </div>
            </Metric>
          </div>
        </Section>

        {/* ── KEY EVENTS ────────────────────────────────── */}
        <Section label="Key Events">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {/* ALC Price Change */}
            <div className="rounded-xl bg-muted/30 p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="size-4 text-muted-foreground/60" />
                  <p className="text-sm font-medium">ALC Price Change</p>
                </div>
                <DlBtn />
              </div>
              <div className="mt-3 flex items-center gap-4">
                <span className="flex items-center gap-1 text-sm">
                  <TrendingUp className="size-3 text-emerald-500" />
                  <span className="font-semibold tabular-nums">
                    {d.alc_price_up_cnt}
                  </span>
                  <span className="text-[10px] text-muted-foreground">UP</span>
                </span>
                <span className="flex items-center gap-1 text-sm">
                  <TrendingDown className="size-3 text-red-400" />
                  <span className="font-semibold tabular-nums">
                    {d.alc_price_down_cnt}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    DOWN
                  </span>
                </span>
              </div>
              <div className="mt-2 border-t border-border/40 pt-2">
                <Row
                  label="Valuation Change"
                  value={fmtINR(d.alc_val_change)}
                />
              </div>
            </div>

            {/* NALC Price Change */}
            <div className="rounded-xl bg-muted/30 p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="size-4 text-muted-foreground/60" />
                  <p className="text-sm font-medium">NALC Price Changes</p>
                </div>
                <DlBtn />
              </div>
              <div className="mt-3 flex items-center gap-4">
                <span className="flex items-center gap-1 text-sm">
                  <TrendingUp className="size-3 text-emerald-500" />
                  <span className="font-semibold tabular-nums">
                    {d.nalc_price_up_cnt}
                  </span>
                  <span className="text-[10px] text-muted-foreground">UP</span>
                </span>
                <span className="flex items-center gap-1 text-sm">
                  <TrendingDown className="size-3 text-red-400" />
                  <span className="font-semibold tabular-nums">
                    {d.nalc_price_down_cnt}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    DOWN
                  </span>
                </span>
              </div>
              <div className="mt-2 border-t border-border/40 pt-2">
                <Row
                  label="Valuation Change"
                  value={fmtINR(d.nalc_val_change)}
                />
              </div>
            </div>

            {/* Expenses */}
            <Metric
              label="Expenses"
              sub="Total Expenses Incurred"
              icon={Receipt}
              tint="bg-red-500/5"
              value={fmtINR(d.expenses)}
              valueColor="text-red-600"
            />

            {/* Discounts */}
            <Metric
              label="Discounts"
              sub="Total Discounts Given"
              icon={Percent}
              tint="bg-purple-500/5"
              value={fmtINR(d.discounts)}
              valueColor="text-purple-600"
            />
          </div>
        </Section>

        {/* ── DELETIONS ─────────────────────────────────── */}
        <Section label="Deletions">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Metric
              label="Sales"
              sub="Invoice"
              icon={Trash2}
              tint="bg-red-500/5"
              action={<DlBtn />}
            >
              <p className="text-2xl font-semibold tabular-nums">
                {fmtNum(d.del_sales_cnt)}
              </p>
              <div className="mt-1">
                <Row label="Value" value={fmtINR(d.del_sales_value)} />
              </div>
            </Metric>

            <Metric
              label="ALC Purchase"
              sub="ALC GRN Deletion Count"
              icon={Package}
              tint="bg-amber-500/5"
              action={<DlBtn />}
            >
              <p className="text-2xl font-semibold tabular-nums">
                {fmtNum(d.del_alc_pur_cnt)}
              </p>
              <div className="mt-1">
                <Row label="Value" value={fmtINR(d.del_alc_pur_val)} />
              </div>
            </Metric>

            <Metric
              label="NALC Purchase"
              sub="NALC GRN Deletion Count"
              icon={Package}
              tint="bg-cyan-500/5"
              action={<DlBtn />}
            >
              <p className="text-2xl font-semibold tabular-nums">
                {fmtNum(d.del_nalc_pur_cnt)}
              </p>
              <div className="mt-1">
                <Row label="Value" value={fmtINR(d.del_nalc_pur_val)} />
              </div>
            </Metric>
          </div>
        </Section>
      </div>
    </div>
  );
}
